import { useQuery } from '@tanstack/react-query';

import { apiService, type DashboardStats } from '../services/api';
import type { DashboardStatsContextFilters } from '../utils/dashboardSearchParams';

/**
 * KPIs e séries do dashboard (publicação). React Query evita corrida entre vários
 * gráficos/cards e mantém cache por período + agências + contexto.
 */
export function useDashboardStats(
  dateRange?: { start: string | null; end: string | null },
  agencies?: string[],
  options?: {
    enabled?: boolean;
    context?: DashboardStatsContextFilters;
  },
) {
  const agenciesKey = agencies?.length ? [...agencies].sort().join(',') : '';
  const rangeKey = `${dateRange?.start ?? ''}|${dateRange?.end ?? ''}`;
  const context = options?.context;
  const contextKey = context
    ? [
        context.assunto ?? '',
        context.states ?? '',
        context.legislation_types ?? '',
        context.change_types ?? '',
      ].join('|')
    : '';
  const enabled = options?.enabled ?? true;

  const query = useQuery<DashboardStats>({
    queryKey: ['dashboard-stats', rangeKey, agenciesKey, contextKey],
    queryFn: () => apiService.getDashboardStats(dateRange, agencies, context),
    enabled,
    staleTime: 30_000,
    keepPreviousData: true,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error instanceof Error ? query.error : query.error ? new Error(String(query.error)) : null,
    refetch: query.refetch,
  };
}
